import type { RouteConfig } from "@asteasolutions/zod-to-openapi";
import { defaultErrorResponses, problemResponse, registry } from "../registry.js";
import { z } from "../zod.js";
import {
  FileCreateRequest,
  FileCreateResponse,
  FileDownloadResponse,
  FileStoreResponse,
} from "./schemas.js";

// The shared-kernel file registry HTTP surface (D-064). Three routes, tagged
// `files` — a platform service, not a module (spec 07 §0). Mirrors
// inventory/register.ts (the `route()` helper; Idempotency-Key on every
// mutation; RFC 9457 problem responses).

const sessionSecurity = [{ sessionCookie: [] }];
const idempotencyHeader = { $ref: "#/components/parameters/IdempotencyKey" };

function route(config: {
  method: "get" | "post";
  path: string;
  operationId: string;
  summary: string;
  request?: RouteConfig["request"];
  responses: RouteConfig["responses"];
  description?: string;
}): void {
  registry.registerPath({
    method: config.method,
    path: config.path,
    operationId: config.operationId,
    tags: ["files"],
    summary: config.summary,
    ...(config.description !== undefined ? { description: config.description } : {}),
    security: sessionSecurity,
    ...(config.request !== undefined ? { request: config.request } : {}),
    ...(config.method !== "get" ? { parameters: [idempotencyHeader] } : {}),
    responses: { ...defaultErrorResponses(), ...config.responses },
  });
}

const IdParam = z.object({ id: z.guid() });

route({
  method: "post",
  path: "/v1/files",
  operationId: "createFile",
  summary: "Register a file and get a presigned upload URL",
  description:
    "Step 1 of 3. Creates a `pending` registry row keyed to the caller's tenant and returns a short-lived presigned S3 PUT. The object key is minted server-side (`files/{tenant_id}/{id}/upload`, and after verification the bytes are frozen at `files/{tenant_id}/{id}/v/{sha256}`) and is never accepted from the caller. `byte_size` and `content_type` are checked against the purpose's cap and allowlist BEFORE presigning; `sha256` is recorded as the client's declaration and verified in step 3. **The PUT MUST send `Content-Type: <the declared content_type>` verbatim** — the header is part of the URL's signature, and the same value is re-checked server-side at step 3, so a PUT that omits or changes it is refused. Session-gated with no permission code: attaching a file to a record is the act that carries authorization, and it is gated by the owning module (see files.controller.ts).",
  request: { body: { content: { "application/json": { schema: FileCreateRequest } } } },
  responses: {
    201: {
      description: "The pending registry row + the presigned PUT",
      content: { "application/json": { schema: FileCreateResponse } },
    },
    422: problemResponse(
      "FILE_PURPOSE_UNKNOWN / FILE_PURPOSE_NOT_UPLOADABLE / FILE_CONTENT_TYPE_NOT_ALLOWED / FILE_TOO_LARGE — the purpose's content-type allowlist and byte cap. FILE_PURPOSE_NOT_UPLOADABLE answers a purpose only the server writes (a rendered document, thread media, a QR kit), which no client may create (D-322).",
    ),
  },
});

route({
  method: "post",
  path: "/v1/files/{id}/store",
  operationId: "storeFile",
  summary: "Verify the uploaded object and mark the file stored",
  description:
    "Step 3 of 3. Reads the object back, records the AUTHORITATIVE byte_size (HeadObject) and sha256 (computed server-side over the bytes), and moves the row to `stored`. A digest, size or CONTENT TYPE that disagrees with the client's declaration moves the row to `rejected` (422 FILE_INTEGRITY_MISMATCH) and the file stays unreadable — a corrupted evidence photo is caught at capture time, when the worker can re-shoot it, rather than at an audit months later. On success the verified bytes are COPIED to a key no presigned PUT can reach and the row is repointed at it, so the recorded digest cannot be invalidated by a later PUT on the still-live upload URL. Idempotent: replaying on a `stored` row returns it unchanged. `rejected` is TERMINAL — replaying on a rejected row is 409 FILE_REJECTED, and a re-shoot is a new file id.",
  request: { params: IdParam },
  responses: {
    200: {
      description: "The stored registry row with authoritative size + digest",
      content: { "application/json": { schema: FileStoreResponse } },
    },
    404: problemResponse("NOT_FOUND — unknown id, and cross-tenant ids are indistinguishable"),
    409: problemResponse(
      "FILE_REJECTED — the row already failed verification and the status is terminal / FILE_STATUS_CONFLICT — a concurrent transition won",
    ),
    422: problemResponse(
      "FILE_NOT_UPLOADED — no object at the key / FILE_INTEGRITY_MISMATCH — digest, size or content type disagrees with the declaration",
    ),
  },
});

route({
  method: "get",
  path: "/v1/files/{id}",
  operationId: "getFile",
  summary: "Resolve a file id to a short-lived download URL (tenant-fenced)",
  description:
    "THE FENCE. The row is read under the caller's tenant GUC, so a file belonging to another tenant is invisible and answers 404 — the same 404 an unknown uuid gets, because a 403 would confirm the id exists somewhere and that is itself the leak (spec 06 §9.10's no-id-oracle rule). The presigned GET is minted from the ROW's object key; no endpoint anywhere accepts an object key from a caller. Generated-document purposes (`quality_coa_pdf`, `quality_coa_logo`, `production_traveler_pdf`, `sales_quote_pdf`, `sales_delivery_note_pdf`) are NOT directly readable here (409 FILE_NOT_DIRECTLY_READABLE) — their owning module serves them after its own permission check.",
  request: { params: IdParam },
  responses: {
    200: {
      description: "The registry row + a short-lived presigned GET",
      content: { "application/json": { schema: FileDownloadResponse } },
    },
    404: problemResponse("NOT_FOUND — unknown id, and cross-tenant ids are indistinguishable"),
    409: problemResponse(
      "FILE_NOT_STORED — the object was never verified / FILE_NOT_DIRECTLY_READABLE — resolve it through the owning module",
    ),
  },
});
